import type { User } from '@/@types/user'
import { LIST_MENTIONS, MENTIONS_SEARCH_USER } from '@/constants/api'
import { post } from '@/utils/request'
import { Global, css } from '@emotion/react'
import { Loading } from '@ruguoapp/jike-fe-components'
import {
	type BeautifulMentionsItem,
	type BeautifulMentionsMenuItemProps,
	type BeautifulMentionsMenuProps,
	BeautifulMentionsPlugin,
	type BeautifulMentionsSearchProps,
} from 'lexical-beautiful-mentions'
import { debounce, uniqBy } from 'lodash-es'
import { AnimatePresence, motion } from 'motion/react'
import { type HTMLAttributes, forwardRef, memo } from 'react'

const search = debounce(
	async (trigger: string, keywords: string | undefined | null) => {
		let users = [] as User[]
		if (keywords === '') {
			users = await post(LIST_MENTIONS).then(
				(res) =>
					uniqBy(
						res.data.data.filter((item: any) => item.type === 'USER'),
						'id'
					) as unknown as User[]
			)
		} else {
			users = await post(MENTIONS_SEARCH_USER, {
				keywords,
			}).then((res) => res.data.data as User[])
		}

		const items = users.map((user) => ({
			data: user,
			id: user.id,
			value: user.screenName,
		})) as unknown as BeautifulMentionsItem[]

		return items
	},
	600,
	{
		leading: true,
		trailing: false,
	}
) as BeautifulMentionsSearchProps['onSearch']

export interface LexicalMentionUserPluginProps extends HTMLAttributes<HTMLDivElement> {}

export default memo(function LexicalMentionUserPlugin({ ...props }: LexicalMentionUserPluginProps) {
	return (
		<>
			<Global
				styles={css`
					.mention-user.focused {
						outline: 2px solid var(--bg-on-body-2);
					}

					.mention-user-menu {
						z-index: 5;
					}
				`}
			/>
			<BeautifulMentionsPlugin
				insertOnBlur={false}
				triggers={['@']}
				menuComponent={MentionUserMenu}
				menuItemComponent={MentionUserMenuItem}
				onSearch={async (trigger, keywords) => (await search(trigger, keywords)) ?? []}
				menuAnchorClassName="mention-user-menu"
			/>
		</>
	)
})

function MentionUserMenu({ loading, children, ...props }: BeautifulMentionsMenuProps) {
	return (
		<motion.ul
			initial={{
				opacity: 0,
			}}
			animate={{
				opacity: 1,
			}}
			exit={{
				opacity: 0,
			}}
			css={css`
				width: 160px;
				height: fit-content;
				font-size: 14px;
				line-height: 20px;
				padding: 4px;
				border-radius: 10px;
				background: var(--bg-on-body-1, #fff);
				box-shadow: 0 2px 12px 0 rgb(0 0 0 / 24%);
			`}
			{...props}
		>
			<AnimatePresence mode="popLayout">
				{loading ? (
					<li
						css={css`
							height: 36px;
							display: flex;
							align-items: center;
							justify-content: center;
						`}
					>
						<Loading />
					</li>
				) : (
					children
				)}
			</AnimatePresence>
		</motion.ul>
	)
}

const MentionUserMenuItem = forwardRef<HTMLLIElement, BeautifulMentionsMenuItemProps>(
	function MentionUserMenuItem({ selected, item, ...props }, ref) {
		const data = item.data?.data as unknown as User
		return (
			<li
				css={css`
					display: flex;
					gap: 4px;
					align-items: center;
					border-radius: 8px;
					padding: 8px;
					cursor: pointer;
					overflow: hidden;
					white-space: nowrap;

					&:hover {
						background: var(--selected-on-body-1);
					}

					${selected &&
					css`
						background: var(--selected-on-body-1);
					`}
				`}
				ref={ref}
				{...props}
			>
				<img
					width={18}
					height={18}
					src={data.avatarImage.thumbnailUrl}
					css={css`
						width: 18px;
						height: 18px;
						border-radius: 18px;
						border: 1px solid var(--solid-separator);
					`}
				/>
				<span
					css={css`
						color: var(--tint-jike-blue);
						font-size: 12px;
						font-weight: 500;
						line-height: 18px;
						letter-spacing: 0.25px;
						padding-right: 3px;
						text-overflow: ellipsis;
						overflow: hidden;
					`}
				>
					{data.screenName}
				</span>
			</li>
		)
	}
)
